<template>
    <div class="playbook-creation-type-selection">
        <div class="radio-container display-flex flex-wrap">
            <AonCard
                v-for="item in playbookTypes"
                class="type-option mb-sm"
                :class="{
                    selected: playbookGenerationStore.playbookSelections[0]?.value === item.value,
                }"
                :key="item.label + '_key'"
                @click="setPlaybookType(item.value, item.label)"
            >
                <div class="content-holder display-flex align-items-start">
                    <div class="icon-holder">
                        <div
                            :id="item.iconID"
                            class="type-option-icon"
                            :style="{
                                backgroundImage: `url(${item.iconBackgroundImg})`,
                            }"
                        ></div>
                        <img
                            src="@/assets/svgs/report/checkmark-circle.svg"
                            class="type-option-icon checkmark"
                        />
                    </div>
                    <div class="label-wrapper ml-md">
                        <p class="label bold" style="text-wrap: nowrap">{{ item.label }}</p>
                        <p class="mt-sm">{{ item.description }}</p>
                    </div>
                </div>
            </AonCard>
        </div>
    </div>
</template>

<script setup>
import { computed } from 'vue'
import { usePlaybookGenerationStore } from '@/stores'
import { useFlag } from '@unleash/proxy-client-vue'

import gloves from '@/assets/icon/gloves.png'
import gavelPaper from '@/assets/icon/gavel-paper.png'
import handshake from '@/assets/icon/handshake.png'
import docAnalysisIcon from '@/assets/icon/doc-analysis.png'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const showLicensingOpportunitiesPlaybook = useFlag('ipAlpha.LicensingOpportunitiesPlaybook')
const showProspectivePatentAnalysisPlaybook = useFlag('ipAlpha.ProspectivePatentAnalysis')

const playbookGenerationStore = usePlaybookGenerationStore()

const playbookTypes = computed(() => {
    let types = [
        {
            label: 'Competitive Matchup',
            description:
                'Identifies the technology areas where the companies you select have innovation coverage. Uncover who has more coverage in an area than another.',
            iconBackgroundImg: gloves,
            value: 'matchUp',
        },
        {
            label: 'Counter Assertion',
            description:
                'Identify technology areas where one party can assert counterclaims against another during litigation. This process focuses on situations where the initiating party has filed a legal action (e.g., alleging patent infringement), and the responding party may defend or retaliate by asserting patents that overlap the initiating parties products.',
            iconBackgroundImg: gavelPaper,
            value: 'counterAssertion',
        },
    ]

    if (showLicensingOpportunitiesPlaybook.value) {
        types.push({
            label: 'Licensing Opportunities',
            description:
                'Discover licensing opportunities by analyzing coverage gaps in peer patent portfolios.',
            iconBackgroundImg: handshake,
            value: 'licensingOpportunities',
        })
    }

    if (showProspectivePatentAnalysisPlaybook.value) {
        types.push({
            label: t('prospectivePatentAnalysis.playbookName'),
            description: t('prospectivePatentAnalysis.playbookDescription'),
            iconBackgroundImg: docAnalysisIcon,
            value: 'prospectivePatentAnalysis',
        })
    }

    return types
})

const setPlaybookType = async (val, label) => {
    playbookGenerationStore.setPlaybookType(val, label)
}
</script>

<style lang="scss" scoped>
.playbook-creation-type-selection {
    height: 100%;
    width: 100%;

    .type-option {
        cursor: pointer;
        outline: solid transparent 3px;
        width: 100%;

        transition: all 0.3s ease-in-out;

        &:last-of-type {
            margin-bottom: 0 !important;
        }

        &:hover {
            background: var(--color-stone-300);
        }

        &.selected {
            border-color: var(--color-success);
            outline-color: var(--color-success);

            .label {
                color: var(--color-moat-600);
            }

            .icon-holder {
                .type-option-icon {
                    opacity: 0;
                }

                .checkmark {
                    opacity: 1;
                }
            }
        }
    }

    .icon-holder {
        position: relative;

        .type-option-icon {
            width: 50px;
            height: 50px;
            background-repeat: no-repeat;
            background-size: contain;

            transition: all 0.3s ease-in-out;
        }

        .checkmark {
            position: absolute;
            top: 0;
            left: 0;

            opacity: 0;
        }
    }

    .label {
        transition: all 0.3s ease-in-out;
    }
}
</style>
